// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Styled, Space, DataLoader} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {useEffect} from '@supermove/hooks';
import {
  ChargeAmountKind,
  ChargeAmountKindOptions,
  DocumentItemInputChargePaymentType,
  OrganizationModel,
  PaymentMethod,
  UserModel,
} from '@supermove/models';

// App
import Callout from '@shared/design/components/Callout';
import DropdownInput from '@shared/design/components/DropdownInput';
import FieldInput from '@shared/design/components/Field/FieldInput';
import Text from '@shared/design/components/Text';
import {
  DocumentContentJsonType,
  SetDocumentContentJsonType,
} from '@shared/modules/Document/types/DocumentTemplateVersionEditorTypes';
import BlueCheckboxField from 'modules/App/components/BlueCheckboxField';
import InputIsRequiredCheckBox from 'modules/Organization/Settings/Document/components/InputIsRequiredCheckBox';

const LoadingIndicator = Styled.Loading`
`;

const Row = Styled.View`
  flex-direction: row;
`;

const NoAvailablePaymentMethodsCallout = () => {
  return (
    <Callout
      text={
        <React.Fragment>
          To start accepting payments, set up{' '}
          <a href={'/settings/company/payments'} target={'_blank'} style={{width: '100%'}}>
            <Text.MicroLink>Supermove Payment</Text.MicroLink>.
          </a>
        </React.Fragment>
      }
    />
  );
};

const AmountCanBeChangedCallout = () => {
  return <Callout text={'The amount charged can be changed when sending this flow.'} />;
};

const InputChargePaymentEditorContent = ({
  organization,
  documentItem,
  documentItemField,
  setDraftDocumentContentJson,
}: {
  organization: OrganizationModel;
  documentItem: DocumentItemInputChargePaymentType;
  documentItemField: string;
  setDraftDocumentContentJson: SetDocumentContentJsonType;
}) => {
  const {paymentMethodIds} = documentItem.input.metadata;

  const creditCardPaymentMethodOptions = PaymentMethod.getCreditCardPaymentMethodOptions({
    organization,
  });

  // We keep the ability to select multiple payment methods, but we only select only one for now.
  const selectedPaymentMethodId =
    paymentMethodIds.length > 0 ? paymentMethodIds[0] : creditCardPaymentMethodOptions[0].value;

  const updatePaymentMethod = (paymentMethodId: string) => {
    setDraftDocumentContentJson((prevState) => {
      _.set(prevState, `${documentItemField}.input.metadata.paymentMethodIds`, [paymentMethodId]);
      return {...prevState};
    });
  };

  useEffect(() => {
    if (selectedPaymentMethodId !== paymentMethodIds[0]) {
      updatePaymentMethod(selectedPaymentMethodId);
    }
  }, [selectedPaymentMethodId, paymentMethodIds]);

  const selectedAmountKind = documentItem.input.metadata.amountKind
    ? documentItem.input.metadata.amountKind
    : ChargeAmountKind.FIXED;

  const sectionedPaymentMethodOptions = [
    {
      label: 'Credit Card',
      options: creditCardPaymentMethodOptions,
    },
  ];

  if (creditCardPaymentMethodOptions.length === 0) {
    return <NoAvailablePaymentMethodsCallout />;
  }

  return (
    <React.Fragment>
      <InputIsRequiredCheckBox
        documentItem={documentItem}
        documentItemField={documentItemField}
        setDraftDocumentContentJson={setDraftDocumentContentJson}
      />
      <Space height={24} />
      <FieldInput
        label={'Select Payment Method'}
        component={DropdownInput}
        input={{
          placeholder: 'Select Payment Method',
          value: selectedPaymentMethodId,
          options: sectionedPaymentMethodOptions,
          setFieldValue: (name: string, value: string) => {
            updatePaymentMethod(value);
          },
          style: {width: '100%'},
        }}
        isClearable={false}
        isRequired
        isResponsive
      />
      <Space height={24} />
      <BlueCheckboxField
        values={{
          allowSelectingExistingPaymentMethods:
            !!documentItem.input.metadata.allowSelectingExistingPaymentMethods,
        }}
        name={'allowSelectingExistingPaymentMethods'}
        label={'Allow selecting existing payment methods'}
        setFieldValue={(name: string, value: boolean) => {
          setDraftDocumentContentJson((prevState: DocumentContentJsonType) => {
            _.set(
              prevState,
              `${documentItemField}.input.metadata.allowSelectingExistingPaymentMethods`,
              value,
            );
            return {...prevState};
          });
        }}
      />
      <Space height={24} />
      <AmountCanBeChangedCallout />
      <Space height={24} />
      <Row>
        <FieldInput
          label={'Default Amount'}
          component={DropdownInput}
          input={{
            placeholder: 'Select Default Amount',
            value: selectedAmountKind,
            options: ChargeAmountKindOptions.map((amountKind) => ({
              label: amountKind.label,
              value: amountKind.value,
            })),
            setFieldValue: (name: string, value: string) => {
              setDraftDocumentContentJson((prevState: DocumentContentJsonType) => {
                _.set(prevState, `${documentItemField}.input.metadata.amountKind`, value);
                return {...prevState};
              });
            },
            style: {width: '100%'},
          }}
          isClearable={false}
          isRequired
          isResponsive
          style={{flex: 1}}
        />
        <Space width={16} />
        <FieldInput
          label={''}
          input={{
            placeholder: 'Enter default amount',
            type: 'number',
            value: documentItem.input.metadata.amount,
            setFieldValue: (name: string, value: string) => {
              setDraftDocumentContentJson((prevState: DocumentContentJsonType) => {
                _.set(prevState, `${documentItemField}.input.metadata.amount`, value);
                return {...prevState};
              });
            },
            style: {width: '100%'},
          }}
          isClearable={false}
          isResponsive
          style={{flex: 1}}
        />
      </Row>
    </React.Fragment>
  );
};

const InputChargePaymentEditor = ({
  documentItem,
  documentItemField,
  setDraftDocumentContentJson,
}: {
  documentItem: DocumentItemInputChargePaymentType;
  documentItemField: string;
  setDraftDocumentContentJson: SetDocumentContentJsonType;
}) => {
  const {data, loading} = useQuery<{
    viewer: UserModel;
  }>(InputChargePaymentEditor.query);

  return (
    <DataLoader data={data} loading={loading} as={LoadingIndicator}>
      {({loadedData}) => (
        <InputChargePaymentEditorContent
          organization={loadedData.viewer.viewingOrganization}
          documentItem={documentItem}
          documentItemField={documentItemField}
          setDraftDocumentContentJson={setDraftDocumentContentJson}
        />
      )}
    </DataLoader>
  );
};

InputChargePaymentEditor.query = gql`
  ${PaymentMethod.getCreditCardPaymentMethodOptions.fragment}

  query InputChargePaymentEditor {
    viewer {
      id
      viewingOrganization {
        id
        ...PaymentMethod_getCreditCardPaymentMethodOptions
      }
    }
  }
`;

export default InputChargePaymentEditor;
